import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionService } from '@app/core/services/permission.service';

import {
  FILTER_ACTIVE_PARAM,
  SearchFormComponent,
} from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  BRAND_SEARCH_CONFIG,
  BRAND_COLUMNS,
  BRAND_DEFAULT_COLUMNS_ORDER,
  BRAND_FLEXIBLE_COLUMNS_ORDER,
  BRAND_ACTION_COLUMN,
  BrandItem,
} from './brand.config';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { BrandService } from './services/brand.service';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';

@Component({
  selector: 'app-brand',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './brand.component.html',
  styleUrls: ['./brand.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandComponent implements OnInit, OnDestroy {
  // Search configuration
  searchConfig = BRAND_SEARCH_CONFIG;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly cdr = inject(ChangeDetectorRef);

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.brand.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.brand.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.brand.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.brand.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.brand.approve');
  });

  // Check if user has any action permissions (UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canApprove() || this.canDelete(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BRAND_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of BRAND_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(BRAND_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BRAND_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of BRAND_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  private readonly secondaryButtonBase: ActionButton = {
    label: 'common.actions.delete',
    severity: 'danger',
    show: true,
    disabled: true,
    icon: 'delete',
  };

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Secondary button for batch delete - always visible if user has delete permission
  secondaryButton = computed(() => ({
    ...this.secondaryButtonBase,
    show: this.canDelete(), // Always show if user has delete permission
    disabled: !this.hasSelectedBrands() || this.hasEnabledBrands(), // Disable if no items selected or if any enabled brands are selected
  }));

  // Data signals
  brands = signal<BrandItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    brandName: '',
    code: '',
    status: null,
    createdName: '',
    manufacturer: '',
    page: 1, // Use 1-based pagination like SPU API service
    size: 20,
  });

  // Selection
  selectedBrands = signal<BrandItem[]>([]);

  // Computed properties
  hasSelectedBrands = computed(() => this.selectedBrands().length > 0);
  hasEnabledBrands = computed(() =>
    this.selectedBrands().some((brand) => brand.status),
  );

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedBrand: BrandItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'batchDelete' | 'statusChange' | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly brandService: BrandService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const urlParams = this.route.snapshot.queryParams;
    const hasSearchParams = urlParams[FILTER_ACTIVE_PARAM] === 'true';

    if (!hasSearchParams) {
      this.loadBrands();
    }

    // Subscribe to brand operation results from add/edit components
    this.brandService.brandOperationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && !hasSearchParams) {
          this.onBrandOperationResult(result);
          // Clear the result after handling it
          this.brandService.clearBrandOperationResult();
        }
      });

    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  onSearch(params: any): void {
    this.searchParams.set({ ...this.searchParams(), ...params, page: 1 });
    this.loadBrands();
  }

  onReset(): void {
    // Check if there's any data to reset
    const currentParams = this.searchParams();
    const hasDataToReset =
      currentParams.brandName ||
      currentParams.code ||
      currentParams.status !== null ||
      currentParams.createdName ||
      currentParams.manufacturer;

    if (!hasDataToReset) {
      return;
    }

    // Reset search parameters to default values
    this.searchParams.set({
      brandName: '',
      code: '',
      status: null,
      createdName: '',
      manufacturer: '',
      page: 1,
      size: 20,
    });

    // Clear any selections
    this.selectedBrands.set([]);

    // Reload brands with default parameters
    this.loadBrands();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1, // Advanced table uses 0-based, convert to 1-based
      size: event.rows,
    });
    this.loadBrands();
  }

  onSelectionChange(selection: BrandItem[]): void {
    this.selectedBrands.set(selection);
    // secondaryButton is a computed signal, so it will update automatically
  }

  onAdd(): void {
    this.router.navigate(['/goods/brand/add']);
  }

  onEdit(brand: BrandItem): void {
    this.router.navigate(['/goods/brand/edit'], {
      queryParams: {
        id: brand.id,
        ...this.route.snapshot.queryParams,
      },
    });
  }

  // Handle brand operation results from add/edit components
  onBrandOperationResult(event: {
    success: boolean;
    message: string;
    type: 'create' | 'update';
  }): void {
    // Force refresh the toast component
    this.forceToastRefresh();

    // Reposition the toast component
    this.repositionToast();

    if (event.success) {
      const summary = this.translateService.instant(
        'brand.messages.success.title',
      );
      const detail = event.message;

      // Show success toast message with delay to ensure component is ready
      setTimeout(() => {
        this.messageService.add({
          severity: 'success',
          summary: summary,
          detail: detail,
          life: 4000,
        });
      }, 100);

      // Refresh the brand list to show the new/updated brand
      this.loadBrands();
    } else {
      const summary = this.translateService.instant(
        'brand.messages.error.title',
      );
      const detail = event.message;

      // Show error toast message with delay to ensure component is ready
      setTimeout(() => {
        this.messageService.add({
          severity: 'error',
          summary: summary,
          detail: detail,
          life: 6000,
        });
      }, 100);
    }
  }

  onDelete(brand: BrandItem): void {
    // Prevent deleting active brands
    if (brand.status) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('brand.messages.warning.title'),
        detail: this.translateService.instant(
          'brand.messages.warning.cannot_delete_active_brands',
        ),
      });
      return;
    }

    this.selectedBrand = brand;
    this.dialogTitle = 'brand.dialog.confirm_delete.title';
    this.dialogMessage = this.translateService.instant(
      'brand.dialog.confirm_delete.message',
      { brandName: brand.brandName },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: 'brand.dialog.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'brand.dialog.actions.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onBatchDelete(): void {
    if (!this.hasSelectedBrands()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('brand.messages.warning.title'),
        detail: this.translateService.instant(
          'brand.messages.warning.select_brands_to_delete',
        ),
      });
      return;
    }

    if (this.hasEnabledBrands()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('brand.messages.warning.title'),
        detail: this.translateService.instant(
          'brand.messages.warning.cannot_delete_active_brands',
        ),
      });
      return;
    }

    this.dialogTitle = 'brand.dialog.confirm_batch_delete.title';
    this.dialogMessage = this.translateService.instant(
      'brand.dialog.confirm_batch_delete.message',
      { count: this.selectedBrands().length },
    );
    this.currentAction = 'batchDelete';
    this.dialogButtons = [
      {
        label: 'brand.dialog.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'brand.dialog.actions.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onStatusChange(brand: BrandItem, newStatus: boolean): void {
    const action = newStatus ? 'activate' : 'deactivate';
    this.selectedBrand = brand;
    this.dialogTitle = 'brand.dialog.confirm_status_change.title';
    this.dialogMessage = this.translateService.instant(
      'brand.dialog.confirm_status_change.message',
      {
        action: this.translateService.instant(`brand.actions.${action}`),
        brandName: brand.brandName,
      },
    );
    this.currentAction = 'statusChange';
    this.dialogButtons = [
      {
        label: 'brand.dialog.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label:
          action === 'activate'
            ? 'brand.dialog.actions.activate'
            : 'brand.dialog.actions.deactivate',
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private loadBrands(): void {
    this.loading.set(true);
    this.brandService.getBrands(this.searchParams()).subscribe({
      next: (response) => {
        this.brands.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('brand.messages.error.title'),
          detail: this.translateService.instant(
            'brand.messages.error.failed_to_load_brands',
          ),
        });
      },
    });
  }

  private deleteBrand(id: string): void {
    this.brandService.deleteBrand(id).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'brand.messages.success.title',
            ),
            detail: this.translateService.instant(
              'brand.messages.success.brand_deleted',
            ),
          });
          this.loadBrands();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'brand.messages.error.title',
            ),
            detail: this.translateService.instant(
              'brand.messages.error.failed_to_delete_brand',
            ),
          });
        }
      },
      error: (error) => {
        console.error('Delete brand error in component:', error);
        this.messageService.add(
          this.brandService.handleBackendValidationError(error, 'delete'),
        );
      },
    });
  }

  private batchDeleteBrands(ids: string[]): void {
    this.brandService.batchDeleteBrands(ids).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'brand.messages.success.title',
            ),
            detail: this.translateService.instant(
              'brand.messages.success.brands_deleted',
            ),
          });
          this.selectedBrands.set([]);
          this.loadBrands();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'brand.messages.error.title',
            ),
            detail: this.translateService.instant(
              'brand.messages.error.failed_to_delete_brands',
            ),
          });
        }
      },
      error: (error) => {
        console.error('Batch delete brands error in component:', error);
        this.messageService.add(
          this.brandService.handleBackendValidationError(error, 'batchDelete'),
        );
      },
    });
  }

  private updateBrandStatus(id: string, status: boolean): void {
    this.brandService.updateBrandStatus(id, status).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'brand.messages.success.title',
            ),
            detail: this.translateService.instant(
              status
                ? 'brand.messages.success.brand_activated'
                : 'brand.messages.success.brand_deactivated',
            ),
          });
          this.loadBrands();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'brand.messages.error.title',
            ),
            detail: this.translateService.instant(
              'brand.messages.error.failed_to_update_status',
            ),
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('brand.messages.error.title'),
          detail: this.translateService.instant(
            'brand.messages.error.failed_to_update_status',
          ),
        });
      },
    });
  }

  // Status methods
  getStatusSeverity(status: boolean): 'success' | 'secondary' {
    return status ? 'success' : 'secondary';
  }

  getStatusDisplayText(status: boolean): string {
    return this.translateService.instant(
      status ? 'brand.status.active' : 'brand.status.inactive',
    );
  }

  getStatusIcon(status: boolean): string {
    return status
      ? 'pi pi-check-circle text-green-500'
      : 'pi pi-times-circle text-gray-500';
  }

  getStatusStyle(status: boolean): any {
    return status
      ? {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        }
      : {
          'background-color': '#f9fafb',
          color: '#6b7280',
          border: 'none',
        };
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'batchDelete') {
        this.confirmBatchDelete();
      } else if (this.currentAction === 'statusChange') {
        this.confirmStatusChange();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedBrand) {
      this.deleteBrand(this.selectedBrand.id);
    }
    this.closeConfirmationDialog();
  }

  confirmBatchDelete(): void {
    const ids = this.selectedBrands().map((brand) => brand.id);
    this.batchDeleteBrands(ids);
    this.closeConfirmationDialog();
  }

  confirmStatusChange(): void {
    if (this.selectedBrand) {
      const newStatus = !this.selectedBrand.status;
      this.updateBrandStatus(this.selectedBrand.id, newStatus);
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedBrand = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent) {
      const placeholder = parent.querySelector('.image-placeholder');
      if (placeholder) {
        (placeholder as HTMLElement).style.display = 'flex';
      }
    }
  }

  // Force refresh the toast component
  private forceToastRefresh(): void {
    // Send a dummy message to wake up the toast component
    this.messageService.add({
      severity: 'info',
      summary: '',
      detail: '',
      life: 1,
    });
    // Clear it immediately
    setTimeout(() => {
      this.messageService.clear();
    }, 10);
  }

  // Reposition the toast component
  private repositionToast(): void {
    // Force a reflow by accessing the toast element
    const toastElement = document.querySelector('p-toast');
    if (toastElement) {
      // Trigger a reflow
      toastElement.getBoundingClientRect();
    }
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
